import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, Search, RotateCcw, Loader2, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, ReadOnlyBanner } from "@/components/common";
import { FileDrop, MappingEditor } from "@/components/FileImport";
import { MonthPicker } from "@/components/DatePicker";
import PayrollTable from "@/components/payroll/PayrollTable";
import PayrollSummary from "@/components/payroll/PayrollSummary";
import PayrollConfirm from "@/components/payroll/PayrollConfirm";
import PayrollFinalized from "@/components/payroll/PayrollFinalized";
import PayrollHistory from "@/components/payroll/PayrollHistory";
import { api, errMsg } from "@/lib/api";
import { currentMonth, monthLabel } from "@/lib/format";
import { calcRow, duplicateCodes, shiftValue, payloadRows, fromSaved, uid } from "@/lib/payroll";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FIELDS = [["code", "Employee ID", true], ["name", "Employee Name", true], ["salary", "Monthly Salary", true], ["days", "Number of Working Days", true], ["hours", "Actual Working Hours", false]];

function Section({ n, title, children, right }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5"><span className="h-6 w-6 rounded-full bg-blue-800 text-white text-xs font-bold grid place-items-center">{n}</span><span className="font-heading font-semibold text-slate-800">{title}</span></div>
        {right}
      </div>
      {children}
    </div>
  );
}

function MethodPicker({ method, setMethod, shift, setShift, disabled }) {
  const opts = [["day", "Day-wise Salary Calculation", "Monthly salary ÷ 30 × working days", CalendarDays], ["hour", "Hour-wise Salary Calculation", "Monthly salary ÷ 30 ÷ shift hours × hours worked", Clock]];
  const bad = method === "hour" && !shiftValue(shift);
  return (
    <div className="grid lg:grid-cols-3 gap-3">
      {opts.map(([k, l, d, Icon]) => (
        <button key={k} disabled={disabled} onClick={() => setMethod(k)} data-testid={`method-${k}`}
          className={`text-left rounded-xl border-2 p-4 transition-colors duration-150 ${method === k ? "border-blue-700 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
          <div className="flex items-center gap-2 font-semibold text-slate-900"><Icon className="h-4 w-4 text-blue-800" /> {l}</div>
          <div className="text-xs text-slate-500 mt-1">{d}</div>
        </button>
      ))}
      {method === "hour" && (
        <div className="rounded-xl border-2 border-dashed border-blue-300 p-4" data-testid="shift-config">
          <Label className="text-xs">Standard Working Hours per Day</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input value={shift} onChange={(e) => setShift(e.target.value)} disabled={disabled} inputMode="decimal" className={`h-10 w-24 text-right money ${bad ? "border-red-500" : ""}`} data-testid="shift-hours-input" />
            <span className="text-sm text-slate-600">hours</span>
          </div>
          {bad ? <div className="text-xs text-red-700 mt-1" data-testid="shift-hours-error">Enter hours between 0 and 24</div> : <div className="text-xs text-slate-500 mt-1">Used for every employee this month</div>}
        </div>
      )}
    </div>
  );
}

export default function Payroll() {
  const { can } = useAuth();
  const writer = can("admin", "payroll");
  const [month, setMonth] = useState(currentMonth());
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(undefined);
  const [rows, setRows] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [file, setFile] = useState(null);
  const [method, setMethod] = useState("day");
  const [shift, setShift] = useState("12");
  const [q, setQ] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [advances, setAdvances] = useState({});
  const codesKey = (rows || []).map((r) => String(r.code || "").trim().toUpperCase()).filter(Boolean).sort().join("|");
  useEffect(() => {
    if (!codesKey) { setAdvances({}); return; }
    const t = setTimeout(() => {
      api.post("/payroll/advance-balances", { codes: codesKey.split("|") }).then((r) => setAdvances(r.data)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [codesKey]);

  const loadRuns = useCallback(() => api.get("/payroll/runs").then((r) => setRuns(r.data)).catch(() => {}), []);
  const loadMonth = useCallback(async (m) => {
    setRun(undefined); setRows(null); setParsed(null); setFile(null);
    try {
      const { data } = await api.get(`/payroll/runs/${m}`);
      setRun(data);
      if (data.status === "reopened") {
        setRows(fromSaved(data)); setMethod(data.method); setShift(data.shift_hours || "12"); setParsed({ filename: data.filename });
      }
    } catch (e) { setRun(null); }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadMonth(month); }, [month, loadMonth]);

  const sh = shiftValue(shift);
  const calcs = useMemo(() => {
    if (!rows) return {};
    const dup = duplicateCodes(rows);
    return Object.fromEntries(rows.map((r) => [r._id, calcRow(r, method, sh, dup)]));
  }, [rows, method, sh]);
  const errorCount = rows ? rows.filter((r) => Object.keys(calcs[r._id].errors).length).length : 0;
  const totals = useMemo(() => {
    const t = { employees: rows?.length || 0, salary: 0, earned: 0, deduction: 0, final: 0 };
    const okRows = (rows || []).filter((r) => !Object.keys(calcs[r._id].errors).length);
    t.employees = okRows.length;
    okRows.forEach((r) => { const c = calcs[r._id]; ["salary", "earned", "deduction", "final"].forEach((k) => { t[k] += c[k]; }); });
    ["salary", "earned", "deduction", "final"].forEach((k) => { t[k] /= 100; });
    return t;
  }, [rows, calcs]);

  const parse = async (f, mapping) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/payroll/parse", fd);
      setFile(f); setParsed(data); setQ("");
      setRows(data.missing_fields.length ? null : data.rows.map((r) => ({ ...r, _id: uid() })));
      if (!data.missing_fields.length) toast.success(`${data.rows.length} employees loaded from ${data.filename}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const change = (id, k, v) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, [k]: v } : r)));
  const remove = (id) => setRows((rs) => rs.filter((r) => r._id !== id));
  const body = () => ({ month, method, shift_hours: shift, rows: payloadRows(rows), filename: parsed?.filename || "" });
  const review = async () => {
    if (errorCount) { toast.error(`Fix ${errorCount} employee row${errorCount > 1 ? "s" : ""} first`); return; }
    setBusy(true);
    try { const { data } = await api.post("/payroll/check", body()); setCheck(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const finalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/finalize", body());
      toast.success(`Payroll for ${data.month_label} finalized`);
      setCheck(null); setRows(null); setParsed(null); setRun(data); loadRuns();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => { setRows(null); setParsed(null); setFile(null); };

  const ql = q.trim().toLowerCase();
  const shown = (rows || []).filter((r) => !ql || String(r.code).toLowerCase().includes(ql) || String(r.name).toLowerCase().includes(ql));
  const locked = run?.status === "finalized";

  return (
    <div className="space-y-5">
      <PageHeader title="Finalize Payroll" subtitle="Upload the month's payroll sheet, choose day-wise or hour-wise calculation, enter deductions and finalize. Salary is always divided by 30." />
      {!writer && <ReadOnlyBanner text="You have view-only access. You can view and download finalized payrolls." />}
      <Section n={1} title="Payroll Month">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-56"><MonthPicker value={month} onChange={setMonth} testid="payroll-month-picker" /></div>
          <span className="text-sm text-slate-500" data-testid="payroll-month-status">
            {run === undefined ? "Loading…" : locked ? <span className="text-emerald-700 font-medium inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> {monthLabel(month)} is finalized</span> : run?.status === "reopened" ? <span className="text-amber-700 font-medium">{monthLabel(month)} reopened for revision by {run.reopened_by}</span> : `No payroll finalized for ${monthLabel(month)} yet`}
          </span>
        </div>
      </Section>
      {run === undefined ? <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-blue-800" /></div>
        : locked ? <PayrollFinalized run={run} isAdmin={can("admin")} onReopened={() => { loadMonth(month); loadRuns(); }} />
        : writer && (
          <>
            <Section n={2} title="Excel File Upload" right={rows && <Button variant="outline" size="sm" onClick={reset} data-testid="payroll-upload-another"><RotateCcw className="h-4 w-4" /> Upload another file</Button>}>
              {rows ? <div className="text-sm text-slate-600" data-testid="payroll-file-info">{parsed?.filename ? <>File: <b>{parsed.filename}</b> · </> : null}{rows.length} employees · original file is not changed</div>
                : <FileDrop onFile={(f) => (/\.(xlsx|xls)$/i.test(f.name) ? parse(f) : toast.error("Please upload an Excel file (.xlsx or .xls)"))} busy={busy} testid="payroll-file" template="payroll" />}
              {!rows && parsed?.missing_fields?.length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="payroll-missing-columns"><AlertTriangle className="h-4 w-4" /> Could not find column(s): {parsed.missing_fields.join(", ")}. Map them below.</div>
                  <MappingEditor key={JSON.stringify(parsed.mapping)} headers={parsed.headers} mapping={parsed.mapping} fields={FIELDS} busy={busy} testid="payroll-map" onApply={(m) => parse(file, m)} />
                </div>
              )}
            </Section>
            {rows && (
              <>
                <Section n={3} title="Salary Calculation Method"><MethodPicker method={method} setMethod={setMethod} shift={shift} setShift={setShift} /></Section>
                <Section n={4} title="Employee Payroll & Deductions" right={<div className="relative w-full sm:w-64"><Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" /><Input placeholder="Search ID or name" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" data-testid="payroll-search" /></div>}>
                  <p className="text-xs text-slate-500 mb-3">Edit any value to correct it. Deductions start at ₹0.00 and are never applied automatically. Leave working hours blank to use days × standard hours.</p>
                  <PayrollTable rows={shown} calcs={calcs} method={method} shift={sh} onChange={change} onRemove={remove} advances={advances} />
                  {errorCount > 0 && <div className="text-sm text-red-700 mt-3" data-testid="payroll-error-count">{errorCount} row{errorCount > 1 ? "s need" : " needs"} fixing before payroll can be finalized.</div>}
                </Section>
                <Section n={5} title="Payroll Summary" right={errorCount > 0 && <span className="text-xs text-red-700" data-testid="payroll-summary-excluded">{errorCount} row{errorCount > 1 ? "s" : ""} with errors not counted</span>}><PayrollSummary totals={totals} /></Section>
                <Button className="w-full h-14 text-base font-semibold bg-blue-800 hover:bg-blue-900" onClick={review} disabled={busy || !rows.length || errorCount > 0 || (method === "hour" && !sh)} data-testid="finalize-payroll-btn">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />} Finalize Payroll
                </Button>
              </>
            )}
          </>
        )}
      <PayrollHistory runs={runs} current={month} onOpen={(m) => { setMonth(m); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      <PayrollConfirm check={check} busy={busy} onClose={() => setCheck(null)} onConfirm={finalize} />
    </div>
  );
}
